import React, { useMemo } from 'react';
import { getPercentageInfo } from '@/pages/opinion/utils';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { chartAllColors } from '@/pages/opinion/constants';
import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import { formatTimeXAxisText, getChartPosition } from '@/pages/opinion/utils/chart';
import { useIntl } from 'umi';
import { DBO_V, DBO_Y } from '@op/utils/format';

export default function LanguageLine({
  timeline,
  height,
  metric,
  filter,
  translate,
}: {
  timeline: { legend: Array<string>; xAxis: Array<string>; series: Array<any> };
  height: number;
  metric: string;
  filter: any;
  translate: (value: string) => string;
}) {
  const { formatMessage } = useIntl();

  const options: any = useMemo(() => {
    const { legend, xAxis, series } = timeline || {};

    if (!xAxis || xAxis.length === 0) {
      return {};
    }
    const isReviews = metric === 'all_reviews';
    const formatValue = isReviews
      ? (value: number) => DBO_V.reviews(value, 2)
      : (value: number) => DBO_V.ratio(value * 100, '-');

    return {
      color: chartAllColors,
      grid: { top: 20 },
      tooltip: {
        appendToBody: true,
        confine: false,
        position: (pos: any, params: any, dom: any, rect: any, size: any) => {
          return getChartPosition(pos, params, dom, rect, size, -5);
        },
        formatter: (params: any) => {
          let currentDateTotal = 0;
          params?.forEach(
            (item: any) => (currentDateTotal += item?.value || 0),
          );
          const sumText = formatMessage({ id: 'Sum' });

          return formatChartParamsDate({
            params,
            total: currentDateTotal,
            isSort: true,
            listTitleFunc: (item: any) => translate(item.seriesName),
            valueFormat: formatValue,
            dealwithRecords(records, total) {
              const newRecords: Array<any> = records.map((item) => {
                const share = getPercentageInfo(item.originValue, total, 2);
                return {
                  ...item,
                  share: share === '-' || share === '0%' ? '-' : share,
                };
              });
              if (isReviews) {
                newRecords.unshift({
                  text: sumText,
                  value: formatValue(total),
                  share: '100%',
                });
              }

              return newRecords.filter((item) => Boolean(item));
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: listTimeTitleFunc(params[0]?.name, filter.date_type),
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({
                    id: isReviews ? 'Reviews' : 'Positive%',
                  }),
                },
              ];
              if (isReviews) {
                columns.push({
                  key: 'share',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: 'Share-1' }),
                });
              }

              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
          });
        },
      },
      legend: {
        data: legend,
        formatter(name: string) {
          return translate(name);
        },
      },
      axisPointer: getAxisPointer((data) => {
        return formatTimeXAxisText(data.value, filter.date_type);
      }),
      xAxis: {
        type: 'category',
        axisLabel: {
          formatter: (value: string) =>
            formatTimeXAxisText(value, filter.date_type),
        },
        boundaryGap: ['20%', 0],
        data: xAxis,
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) => {
            return DBO_Y.reviewsPercent(value, !isReviews);
          },
        },
      },
      series,
    };
  }, [timeline, formatMessage]);

  return (
    <EChartCommon
      height={height}
      leftTitle={
        <div className="dbo-all-reviews-chart--left-title">
          <span className="left-title--text">
            {formatMessage({
              id: metric === 'all_reviews' ? 'Reviews' : 'Positive%',
            })}
          </span>
        </div>
      }
      options={options}
      optionsProps={{ type: 'line' }}
      className={'dbo-all-reviews-chart'}
    ></EChartCommon>
  );
}
